import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { MenuItem, TextField } from '@mui/material'
import React from 'react'
import Drawer from '../../../components/Drawer'
import { RamoMark, selectMenu } from '../common/policy-common'
import { AVISO, MAX_COMENTARIO, MIN_COMENTARIO, usePolicyCloseModel } from './policy-close-model'
import type { PolicyCloseProps } from './policy-close-model'

export type { PolicyCloseProps } from './policy-close-model'

const PolicyClose = (props: PolicyCloseProps) => {
    const { polizaId, onClose } = props
    const model = usePolicyCloseModel(props)
    const { poliza, form, loading } = model

    if (!polizaId) return null

    return (
        <Drawer open={Boolean(polizaId)} onClose={onClose} bare width={500}>
            <form className="flex h-full flex-col bg-k-surface" onSubmit={model.submit}>
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-k-line px-6 py-5">
                    <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-k-danger-soft text-k-danger-ink">
                            <Inventory2OutlinedIcon style={{ fontSize: 22 }} />
                        </span>
                        <div className="flex min-w-0 flex-col gap-0.5">
                            <h2 className="m-0 text-xl font-bold text-k-ink">{model.title}</h2>
                            <span className="text-[13px] text-k-muted">La póliza sale de seguimiento y queda en el historial del cliente.</span>
                        </div>
                    </div>
                    <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-k-surface hover:bg-k-hover" onClick={onClose}>
                        <CloseIcon style={{ fontSize: 18 }} />
                    </button>
                </div>

                {model.done ? (
                    <div role="status" className="flex flex-1 flex-col items-start gap-3.5 px-7 py-10">
                        <span className="flex h-14 w-14 items-center justify-center rounded-[18px] bg-k-line text-k-ink-2">
                            <CheckIcon style={{ fontSize: 26 }} />
                        </span>
                        <h3 className="m-0 text-[22px] font-extrabold text-k-ink">Póliza {poliza?.noPoliza || ''} cerrada</h3>
                        <p className="m-0 text-sm leading-relaxed text-k-muted">
                            Razón: {model.razonNombre || '—'}. El comentario quedó en la bitácora.
                        </p>
                        <button
                            type="button"
                            className="mt-1 h-11 rounded-xl border border-k-line-strong bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                            onClick={onClose}
                        >
                            Listo
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-[22px]">
                            {loading && (
                                <div className="flex flex-col gap-3">
                                    <div className="h-28 animate-pulse rounded-[14px] bg-k-line" />
                                    <div className="h-12 animate-pulse rounded-xl bg-k-line" />
                                    <div className="h-32 animate-pulse rounded-xl bg-k-line" />
                                </div>
                            )}

                            {model.error && !loading && (
                                <div role="alert" className="flex gap-2.5 rounded-xl bg-k-danger-soft px-3.5 py-3 text-sm font-semibold text-k-danger-ink">
                                    <ErrorOutlineIcon className="shrink-0" style={{ fontSize: 18 }} />
                                    <span>{model.error}</span>
                                </div>
                            )}

                            {!loading && poliza && (
                                <>
                                    <dl className="m-0 flex flex-col gap-3.5 rounded-[14px] border border-k-line bg-k-sunken px-[18px] py-4">
                                        <div className="flex items-center gap-3">
                                            <RamoMark ramo={poliza.ramo} boxed />
                                            <div>
                                                <dt className="text-xs font-bold text-k-accent-ink">No. póliza</dt>
                                                <dd className="m-0 mt-0.5 text-[15px] font-semibold tabular-nums text-k-ink">{poliza.noPoliza || '—'}</dd>
                                            </div>
                                        </div>
                                        <div>
                                            <dt className="text-xs font-bold text-k-accent-ink">Concepto</dt>
                                            <dd className="m-0 mt-0.5 text-[15px] font-semibold text-k-ink">{poliza.concepto || '—'}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-xs font-bold text-k-accent-ink">Contratante</dt>
                                            <dd className="m-0 mt-0.5 text-[15px] font-semibold text-k-ink">{model.contratante || '—'}</dd>
                                        </div>
                                        {(model.fin || model.prima) && (
                                            <div className="flex gap-6">
                                                {model.fin && (
                                                    <div>
                                                        <dt className="text-xs font-bold text-k-accent-ink">Fin de vigencia</dt>
                                                        <dd className="m-0 mt-0.5 text-sm font-semibold text-k-ink">{model.fin}</dd>
                                                    </div>
                                                )}
                                                {model.prima && (
                                                    <div>
                                                        <dt className="text-xs font-bold text-k-accent-ink">Prima actual</dt>
                                                        <dd className="m-0 mt-0.5 text-sm font-semibold tabular-nums text-k-ink">{model.prima}</dd>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </dl>

                                    <TextField
                                        className="v2-field v2-select"
                                        variant="filled"
                                        fullWidth
                                        select
                                        required
                                        label="Razón de cierre"
                                        name="razon"
                                        value={form.razon}
                                        error={model.razonError}
                                        helperText={model.razonError ? 'Selecciona la razón de cierre.' : undefined}
                                        onChange={(event) => model.changeRazon(event.target.value)}
                                        slotProps={{ select: selectMenu }}
                                    >
                                        <MenuItem value="" disabled><em>Selecciona una razón</em></MenuItem>
                                        {model.razones.map((razon) => (
                                            <MenuItem key={razon.id} value={String(razon.id)}>{razon.nombre}</MenuItem>
                                        ))}
                                    </TextField>

                                    <div className="flex flex-col gap-1.5">
                                        <TextField
                                            className="v2-field"
                                            variant="filled"
                                            fullWidth
                                            required
                                            multiline
                                            minRows={5}
                                            maxRows={10}
                                            label="Comentarios"
                                            name="observaciones"
                                            value={form.observaciones}
                                            error={model.comentarioError}
                                            placeholder="Ej. El cliente vendió el vehículo el 10 de octubre; no requiere nueva póliza."
                                            onChange={(event) => model.changeObservaciones(event.target.value)}
                                        />
                                        <div className={`flex justify-between gap-2.5 text-xs font-medium ${model.comentarioError ? 'text-k-danger' : 'text-k-muted'}`}>
                                            <span>{model.comentarioError ? `Explica el motivo con al menos ${MIN_COMENTARIO} caracteres.` : 'Queda en la bitácora de la póliza y del cliente.'}</span>
                                            <span className="tabular-nums">{form.observaciones.length}/{MAX_COMENTARIO}</span>
                                        </div>
                                    </div>

                                    <div className="flex gap-2.5 rounded-xl bg-[#FFF0DB] px-3.5 py-3 text-[13px] leading-relaxed text-[#7A4A06]">
                                        <WarningAmberOutlinedIcon className="mt-0.5 shrink-0" style={{ fontSize: 18 }} />
                                        <span>{AVISO}</span>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="flex shrink-0 gap-2.5 border-t border-k-line px-6 py-4">
                            <button
                                type="button"
                                className="h-[52px] flex-1 rounded-[14px] border border-k-line-strong bg-k-surface text-[15px] font-bold text-k-ink hover:bg-k-hover disabled:opacity-50"
                                onClick={onClose}
                                disabled={model.saving}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={model.saving || loading || !poliza}
                                className="h-[52px] flex-[2] rounded-[14px] bg-k-danger text-[15px] font-extrabold tracking-[0.04em] text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {model.saving ? 'Guardando…' : model.submitLabel}
                            </button>
                        </div>
                    </>
                )}
            </form>
        </Drawer>
    )
}

export default PolicyClose
